<template>
    <div>
        <h2 class="text-2xl font-semibold text-black font-heading">
            {{ translations.title }}
        </h2>

        <div class="lg:grid lg:grid-cols-12 lg:gap-x-16">
            <div
                class="mt-10 text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 lg:mt-9 xl:col-start-9"
            >
                <div class="flex items-center text-black">
                    <button
                        type="button"
                        @click="prevMonth"
                        class="-m-1.5 flex items-center justify-center p-1.5 text-secondary"
                    >
                        <svg
                            class="size-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </button>

                    <div class="flex-auto text-sm font-semibold font-heading">
                        {{ formattedMonth }}
                    </div>

                    <button
                        type="button"
                        @click="nextMonth"
                        class="-m-1.5 flex items-center justify-center p-1.5 text-secondary"
                    >
                        <svg
                            class="size-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </button>
                </div>

                <div class="mt-6 grid grid-cols-7 text-xs/6 text-secondary">
                    <div v-for="(day, i) in translations.weekdays" :key="i">
                        {{ day }}
                    </div>
                </div>

                <div
                    class="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gardenia text-sm shadow ring-1 ring-gardenia"
                >
                    <div
                        v-for="(day, i) in days"
                        :key="i"
                        @click="onDayClick(day)"
                        class="cursor-pointer py-1.5 hover:bg-gray-100 focus:z-10"
                        :class="{
                            '!bg-accent text-white': isSelected(day),
                            'bg-white text-black': isSameMonth(
                                day,
                                currentMonth,
                            ),
                            'bg-gray-50 text-secondary': !isSameMonth(
                                day,
                                currentMonth,
                            ),
                            'font-semibold': isToday(day),
                            'text-accent':
                                isToday(day) && isSameMonth(day, currentMonth),
                        }"
                    >
                        <time
                            :datetime="day.toISOString()"
                            class="mx-auto flex size-7 items-center justify-center rounded-full relative"
                        >
                            {{ day.getDate() }}
                            <span
                                v-if="hasServiceOnDay(day)"
                                class="absolute bottom-0 right-0 h-1.5 w-1.5 rounded-full bg-accent-dark"
                            />
                        </time>
                    </div>
                </div>
            </div>

            <!-- Filtered list -->
            <div class="mt-4 lg:col-span-7 xl:col-span-8">
                <!-- Church filter -->
                <div class="mb-4 text-left">
                    <ChurchSelector
                        v-model="selectedChurch"
                        :label="translations.filterLabel"
                        :placeholder="translations.selectChurch"
                        :options="availableChurches"
                    />
                </div>

                <!-- List of services -->
                <div v-if="filteredServices.length > 0">
                    <ol class="divide-y divide-gardenia text-sm/6">
                        <li
                            v-for="(service, i) in filteredServices"
                            :key="i"
                            class="flex gap-x-6 py-6"
                        >
                            <!-- <img
                                :src="service.image"
                                alt=""
                                class="size-14 flex-none rounded-full"
                            /> -->
                            <div class="flex-auto">
                                <h3
                                    class="pr-10 font-semibold text-black font-heading"
                                >
                                    {{ service.church }} — {{ service.town }}
                                </h3>
                                <p class="text-secondary">
                                    {{ service.address }}
                                </p>
                                <dl
                                    class="mt-2 flex flex-col text-secondary xl:flex-row"
                                >
                                    <div class="flex items-start gap-x-3">
                                        <dt class="sr-only">Date</dt>
                                        <dd>
                                            <time :datetime="service.datetime">
                                                {{
                                                    new Date(
                                                        service.datetime,
                                                    ).toLocaleString()
                                                }}
                                            </time>
                                        </dd>
                                    </div>
                                    <div
                                        class="mt-2 xl:ml-4 xl:mt-0 xl:border-l xl:border-secondary/50 xl:pl-4"
                                    >
                                        <dt class="sr-only">Priest</dt>
                                        <dd>{{ service.priest }}</dd>
                                    </div>
                                </dl>
                            </div>
                        </li>
                    </ol>
                </div>

                <div
                    v-else
                    class="lg:col-span-7 xl:col-span-8 text-center mt-12 p-6 bg-gardenia rounded-lg text-slate-800 text-sm font-sans self-center"
                >
                    {{ translations.noServices }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useCalendar } from "~/composables/useCalendar";
import ChurchSelector from "./ChurchSelector.vue";

const props = defineProps<{
    locale: string;
    translations: {
        title: string;
        filterLabel: string;
        selectChurch: string;
        noServices: string;
        weekdays: string[];
        months: string[];
    };
    services: Record<
        string,
        {
            churchUuid: string;
            town: string;
            church: string;
            address: string;
            priest: string;
            datetime: string;
            image: string;
        }[]
    >;
}>();

const { currentMonth, days, nextMonth, prevMonth, isSameMonth, isToday } =
    useCalendar();

const selectedDate = ref<string | null>(null);
const selectedChurch = ref("");
const firstAvailableDate = Object.keys(props.services).sort()[0] ?? null;

const availableChurches = computed<[string, string][]>(() => {
    const all = Object.values(props.services).flat();

    const uniqueByUuid = new Map<string, string>();
    for (const { churchUuid, church, town } of all) {
        if (!uniqueByUuid.has(churchUuid)) {
            uniqueByUuid.set(churchUuid, `${church} — ${town}`);
        }
    }

    return Array.from(uniqueByUuid.entries());
});

if (firstAvailableDate) {
    selectedDate.value = firstAvailableDate;
}

const filteredServices = computed(() => {
    const key = selectedDate.value;
    if (!key || !props.services[key]) return [];

    let filtered = props.services[key];
    if (selectedChurch.value) {
        filtered = filtered.filter(
            (s) => s.churchUuid === selectedChurch.value,
        );
    }

    return filtered;
});

function formatMonthYear(date: Date): string {
    return `${props.translations.months[date.getMonth()]} ${date.getFullYear()}`;
}

function formatDateKey(date: Date): string {
    return date.toLocaleDateString("sv-SE"); // → YYYY-MM-DD guaranteed
}

function onDayClick(date: Date) {
    selectedDate.value = formatDateKey(date);
}

function isSelected(date: Date) {
    return selectedDate.value === formatDateKey(date);
}

function hasServiceOnDay(date: Date): boolean {
    const key = formatDateKey(date);
    const dayServices = props.services[key];

    if (!dayServices) return false;

    if (!selectedChurch.value) return true; // show all if no filter applied

    return dayServices.some((s) => s.churchUuid === selectedChurch.value);
}

const formattedMonth = computed(() => formatMonthYear(currentMonth.value));
</script>
